<template>
  <div id="page-product-form">
    <vs-row>
      <vs-col :class="readonly ? 'readonly-form' : ''">
        <card-page
            :card-title="cardTitle"
            :switch-model="model.IsEnabled"
        >
          <vue-element-loading :active="busy"></vue-element-loading>
          <vue-form-generator
              ref="form"
              :model="model"
              :options="formOptions"
              :schema="schema"
          />
        </card-page>
        <card-page card-title="News For Specific Entities">
          <additive-widget
            ref="formCoverHolders"
            v-model="model"
            :form-options="formOptions"
            :schema="entitiesSchema"
            label="News For Specific Entities"
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>

import FormModelMixin from '@/components/mixins/FormModelMixin';
import CardPage from '@/views/components/Views/CardPage';
import {selectOptions, datepickerOptions, arrayOptions} from '@/components/form/FormElementOption';
import moment from 'moment';
import AdditiveWidget from '@/components/form/fields/AdditiveWidget';
import FormFieldValidationMixin from '@/components/mixins/FormFieldValidationMixin';

export default {
  name: 'ProductForm',
  components: {
    CardPage,
    AdditiveWidget,
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  mixins: [FormModelMixin, FormFieldValidationMixin],
  data() {
    return {
      disableEntities: false,
      model: {
        applications: {},
        IsNewsForAll: false,
        NewsValidFrom: null,
      },
      schema: {
        groups: [
          {
            legend: 'General',
            styleClasses: 'w-full',
            fields: [
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Title',
                model: 'NewsTitle',
                required: () => !this.readonly,
                validator: ['required', 'string']
              },
              {
                type: 'textArea',
                styleClasses: 'w-full',
                label: 'Content',
                model: 'NewsContent',
                rows: 12,
                required: () => !this.readonly,
                validator: ['required', 'string']
              },
              {
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Sales Country',
                model: 'SalesCountryType_FK',
                values: () => this.countryOptions,
                selectOptions,
                required: true,
                validator: 'required'
              },
              {
                type: 'vsCheckbox',
                label: 'Display News For All',
                styleClasses: 'w-full',
                model: 'IsNewsForAll',
              }
            ]
          },
          {
            legend: 'Dates',
            styleClasses: 'w-full checkbox-group',
            fields: [
              {
                ...datepickerOptions,
                styleClasses: 'vx-col w-1/6',
                label: 'Valid From',
                model: 'NewsValidFrom',
                required: () => !this.readonly,
                onChanged: () => {
                  this.validateField('NewsValidTo');
                },
                validator: [
                  'required',
                  (value, field, model) => {
                    if (model.NewsValidTo) {
                      let newsValidTo = model.NewsValidTo;
                      let newsValidFrom = moment(model.NewsValidFrom).unix();

                      if (typeof newsValidTo === 'string') {
                        newsValidTo = moment(
                          model.NewsValidTo,
                          'YYYY-MM-DD'
                        ).unix();
                      } else {
                        newsValidTo = newsValidTo / 1000;
                      }

                      if (newsValidFrom > newsValidTo) {
                        return 'Inception Date must be before Expiry Date.';
                      }
                    }

                    return true;
                  }
                ]
              },
              {
                ...datepickerOptions,
                styleClasses: 'vx-col w-1/6',
                label: 'Valid to',
                model: 'NewsValidTo',
                onChanged: () => {
                  this.validateField('NewsValidFrom');
                },
                validator: [
                  'string',
                  (value, field, model) => {
                    if (model.NewsValidFrom) {
                      let newsValidFrom = model.NewsValidFrom;
                      let newsValidTo = moment(model.NewsValidTo).unix();

                      if (typeof newsValidFrom === 'string') {
                        newsValidFrom = moment(
                          model.NewsValidFrom,
                          'YYYY-MM-DD'
                        ).unix();
                      } else {
                        newsValidFrom = newsValidFrom / 1000;
                      }

                      if (newsValidTo < newsValidFrom) {
                        return 'Expiry Date must be after Inception Date.';
                      }
                    }

                    return true;
                  }
                ]
              }  
            ]
          },
        ],
      },
      entitiesSchema: {
        groups: [
          {
            styleClasses: 'w-full pt-3',
            fields: [
              {
                model: 'companies',
                hideAddButton: false,
                showRemoveButton: true,
                ...arrayOptions,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            disabled: () => this.disableEntities,
                            type: 'vueMultiSelect',
                            inputType: 'text',
                            styleClasses: 'w-1/3',
                            label: 'Company Name',
                            model: 'Company_FK',
                            values: () => this.companyOptions,
                            selectOptions,
                            required: !this.readonly,
                            validator: 'selectRequiredUuid',
                          },
                        ]
                      }
                    ]
                  }
                }
              }
            ]
          }
        ]
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
      companyOptions: [],
      countryOptions: [],
      options: [
        {
          endpoint: () => this.$api.companies.list(),
          optionsFieldName: 'companyOptions',
          nameFieldName: 'CompanyName',
          idFieldName: 'Company_FK',
          modelArrayName: 'companies',
        },
        {
          endpoint: () => this.$api.contentManagementNews.getSalesCountries(),
          optionsFieldName: 'countryOptions',
          nameFieldName: 'Type',
          idFieldName: 'SalesCountryType_FK',
        },
      ]
    };
  },

  watch: {
    'model.IsNewsForAll': {
      handler(newValue) {
        this.disableEntities = !!newValue;

        this.$nextTick(() => {
          const hideButtons = this.$el.querySelectorAll('.remove-button, .add-button');
          const removeBorders = this.$el.querySelectorAll('.form-array-editor span');
          
          if (hideButtons.length > 0) {
            hideButtons.forEach(button => {
              if (this.disableEntities) {
                button.classList.add('hidden');
              } else {
                button.classList.remove('hidden');
              }
            });
          }
          
          if (removeBorders.length > 0) {
            removeBorders.forEach(button => {
              if (this.disableEntities) {
                button.classList.add('border-none');
              } else {
                button.classList.remove('border-none');
              }
            });
          }
        });
      },
      immediate: true,
    },
    'value.NewsValidFrom': {
      handler(newValue, oldValue) {
        if (newValue && !this.model.NewsValidFrom) {
          this.model.NewsValidFrom = this.value.NewsValidFrom;
        }
      },
      immediate: true,
    },
  },

  async mounted() {
    this.listApplications();
  },

  methods: {
    async listApplications() {
      try {
        const { data } = await this.$api.applications.list();
        const applications = data.data;
        const checkboxSchema = {
          legend: 'Applications',
          styleClasses: 'w-full checkbox-group',
          fields: []
        };

        applications.forEach((application) => {
          checkboxSchema.fields.push({
            // disabled: () => this.disableEntities,
            type: 'vsCheckbox',
            label: application.ApplicationName,
            styleClasses: 'w-3/12 field',
            model: `applications.application_${application.id}`,
            onChanged: (model) => {
              if (!model.applications[`application_${application.id}`]) {
                delete this.model.applications[`application_${application.id}`];                
              }
            },
          });
        });

        this.schema.groups.push(checkboxSchema);
      } catch (e) {
        console.log(e);
      }
    },
  },
};
</script>
